import React, { useCallback, useEffect, useState } from 'react'
import { Alert, Button, Spin, Table, Tag, Typography, message } from 'antd'
import { LoadingOutlined } from '@ant-design/icons'
import { createBillingPayment, getBillingPayments } from '../settings/modules/api'
import { mapPaymentStatus } from '../../utilits/payment-status'

const { Title, Text } = Typography

function fmtDate(v: string | null) {
  if (!v) return '—'
  return new Date(v).toLocaleDateString('ru-RU')
}

function modulesLabel(row: any): string {
  let snap = row.modules_snapshot
  if (typeof snap === 'string') {
    try {
      snap = JSON.parse(snap)
    } catch {
      snap = null
    }
  }
  if (Array.isArray(snap) && snap.length) {
    return snap.map((m: any) => m.name || m.code).join(', ')
  }
  return '—'
}

export default function BillingPage() {
  const [payments, setPayments] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [paying, setPaying] = useState(false)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getBillingPayments()
      setPayments(res.data || [])
    } catch (e: any) {
      setError(e?.message || 'Не удалось загрузить оплаты')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const onPay = async () => {
    setPaying(true)
    try {
      await createBillingPayment()
      message.success('Счёт на период создан')
      await load()
    } catch (e: any) {
      message.error(e?.message || 'Не удалось создать оплату')
    } finally {
      setPaying(false)
    }
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: 64 }}>
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32 }} spin />} />
      </div>
    )
  }

  const columns = [
    {
      title: 'Дата',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (v: string) => (v ? new Date(v).toLocaleString('ru-RU') : '—'),
    },
    {
      title: 'Период',
      key: 'period',
      render: (_: any, row: any) => `${fmtDate(row.period_start)} — ${fmtDate(row.period_end)}`,
    },
    {
      title: 'Тариф',
      dataIndex: 'billing_term_months',
      key: 'term',
      render: (v: number) => (v ? `${v} мес.` : '—'),
    },
    {
      title: 'Модули',
      key: 'modules',
      render: (_: any, row: any) => modulesLabel(row),
    },
    {
      title: 'Сумма',
      dataIndex: 'amount',
      key: 'amount',
      render: (v: number) => `${Number(v || 0).toLocaleString('ru-RU')} ₽`,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (v: string) => {
        const s = mapPaymentStatus(String(v || ''))
        return <Tag color={s.color}>{s.label}</Tag>
      },
    },
  ]

  return (
    <div style={{ padding: 24, maxWidth: 1000, margin: '0 auto' }}>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: 24,
        }}
      >
        <Title level={3} style={{ margin: 0 }}>
          Биллинг
        </Title>
        <Button type="primary" loading={paying} onClick={onPay}>
          Оплатить период
        </Button>
      </div>

      <Text type="secondary" style={{ display: 'block', marginBottom: 16 }}>
        История оплат по факту: когда, сколько, за какие модули и статус от платёжной системы.
      </Text>

      {error && <Alert type="error" showIcon message={error} style={{ marginBottom: 16 }} />}

      <Table
        dataSource={payments}
        columns={columns}
        rowKey="id"
        pagination={{ pageSize: 20 }}
        size="small"
      />
    </div>
  )
}
